import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  Renderer2,
  DoCheck,
} from '@angular/core';
import { AuthService } from '../../shared/services/auth.service';
import { DriveRightRailService } from '../../shared/drive/drive-right-rail.service';
import { HeaderInfo, ProfileSettings, ShareLinkAssetEntityTypes, ThemeOptions, User } from '../../shared/interfaces';
import { EmitterService } from '../../shared/services/emitter.service';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ActivatedRoute, NavigationStart, Router, NavigationEnd } from '@angular/router';
import { LeftSidebarFloatingComponent } from '../left-sidebar-floating/left-sidebar-floating.component';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { WorkspaceService } from '../../shared/services/workspace.service';
import { ReportingService } from '../../shared/services/reporting.service';
import { SelfTrainerV2Service } from '../../shared/services/self-trainer-v2.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SubsectionService } from '../../shared/services/subsection.service';
import { TopMediaInfoService } from '../../shared/components/top-media-info/top-media-info.service';
import { PlatformCommentsChatStateService } from '../../shared/services/platform-comments-chat-state.service';
import { DriveOrgCommentsUiService } from '../../shared/drive/drive-org-comments-ui.service';
import { isBrandlessBrandPage, isOrgDriveUrl } from '../../shared/drive/org-entity-routes';
import { PlatformTaskInterfaceStateService } from '../../shared/services/platform-task-interface-state.service';
import { ActivePage, AppStateService } from '../../shared/services/app-state.service';
import { BaseTreeNode, isShareLinkAssetNode, isSubsectionFileNode } from '../../project-subsection/models/tree-node';
import { TreeState } from '../../project-subsection/models';
import { TreeManagerService } from '../../project-subsection/services/tree-manager.service';
import { CommentPanelWidthService } from '../../shared/services/comment-panel-width.service';
import {
  CommentComponentViewService,
  CommentWrapperContext,
} from '../../shared/services/comment-component-view.service';
import { CommentVisibilityService } from '../../shared/services/comment-visibility.service';
import { ProjectService } from '../../shared/services/project.service';
import { BrandService } from '../../shared/services/brand.service';

const urlPath = (url: string): string => url.split(/[?#]/)[0];

@Component({
  selector: 'child-layout',
  templateUrl: './child-layout.component.html',
  styleUrl: './child-layout.component.scss',
  standalone: false,
})
export class ChildLayoutComponent implements OnInit, OnDestroy, DoCheck {
  @ViewChild('rootLayoutContainer', { static: true }) mainLayoutContainer: ElementRef;
  @ViewChild(LeftSidebarFloatingComponent) leftSidebarFloatingComponent!: LeftSidebarFloatingComponent;

  private readonly authService = inject(AuthService);
  protected readonly driveRightRail = inject(DriveRightRailService);
  readonly projectService = inject(ProjectService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly workspaceService = inject(WorkspaceService);
  private readonly reportingService = inject(ReportingService);
  private readonly selfTrainerV2Service = inject(SelfTrainerV2Service);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly modalService = inject(NgbModal);
  private readonly subsectionService = inject(SubsectionService);
  private readonly topMediaInfoService = inject(TopMediaInfoService);
  readonly platformChatState = inject(PlatformCommentsChatStateService);
  private readonly driveOrgCommentsUi = inject(DriveOrgCommentsUiService);
  readonly platformTaskState = inject(PlatformTaskInterfaceStateService);
  private readonly appState = inject(AppStateService);
  private readonly treeManagerService = inject(TreeManagerService);
  readonly commentPanelWidthService = inject(CommentPanelWidthService);
  private readonly commentComponentViewService = inject(CommentComponentViewService);
  private readonly commentVisibilityService = inject(CommentVisibilityService);
  private readonly brandService = inject(BrandService);
  private readonly cdr = inject(ChangeDetectorRef);
  authUser: User;
  authUserSetting: any;
  publicPage: boolean = false;
  publicReviewLink: boolean = false;
  isCampaignDrivePath = false;
  isComparePage = false;
  isTasksPage = false;
  publicShareLink: boolean = false;
  cacheBuster: any = Date.now().toString();
  ngUnsubscribe = new Subject();
  openedLeftMenu: boolean = false;
  @Input() themeOptions: ThemeOptions;
  @Input() headerInfo: HeaderInfo;
  @Input() userProfile: ProfileSettings;
  @Input() campaignName: string;
  isReportingView: boolean = false;
  get headerBgImage(): string {
    return this.themeOptions?.backgroundImage ?? '';
  }
  showMobileSearch = false;
  hasReportMenus = false;
  hasSelfTrainerMenus = false;
  isAgencyUser = false;
  private routerSub!: Subscription;
  commentsCollapsed: boolean = true;
  isShareLinkPreview: boolean = false;
  isReleasePreview: boolean = false;
  isCommentFeatureAllowed: boolean = false;
  private readonly platformChatBodyClass: string = 'platform-comment-chat-active';
  private isPlatformChatBodyClassApplied: boolean = false;
  shouldHideCommentWrapper = false;
  shouldHideTaskWrapper = false;

  isCommentLoading: boolean = false;
  private reserveCommentPanelWidthDuringNavigation = false;
  private reserveCommentPanelWidthTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Expose service properties for template
  get commentPanelWidth(): number {
    return this.commentPanelWidthService.width();
  }

  get maxViewportPercent(): number {
    return 0.5;
  }

  get sashIsFullMode(): boolean {
    return this.commentPanelWidthService.isFullMode();
  }

  set sashIsFullMode(value: boolean) {
    this.commentPanelWidthService.setFullMode(value);
  }

  get isTransitioningFullSize(): boolean {
    return this.commentPanelWidthService.isTransitioningFullSize();
  }

  get isResizing(): boolean {
    return this.commentPanelWidthService.isResizing();
  }

  get isFullscreen() {
    return this.topMediaInfoService.isFullscreen;
  }

  get isOnProjectSubsection(): boolean {
    return this.topMediaInfoService.isOnProjectSubsection;
  }

  get isKpfWidgetExpanded(): boolean {
    return this.topMediaInfoService.isKpfWidgetExpanded;
  }

  get canNavigateAssets(): boolean {
    return !!this.treeState?.root?.children?.length;
  }

  get brandName(): string | null {
    return this.authService.getMyBrand()?.name;
  }

  get brandLogo(): string | null {
    return this.authService.getMyBrand()?.logo;
  }

  isMobileSize: boolean = false;
  private previousMobileSize: boolean = false;

  @HostListener('window:resize', ['$event'])
  onWindowResize(_event: UIEvent): void {
    this.checkMobileSize();
    this.handleWindowResizeForFullMode();
  }

  private handleWindowResizeForFullMode(): void {
    if (!this.isMobileSize && this.isCommentPanelFullSize) {
      const viewportWidth: number = this.document.defaultView?.innerWidth ?? 0;
      if (viewportWidth > 0) {
        this.commentPanelWidthService.handleWidthChange(viewportWidth, {
          isCommentLoading: this.isCommentLoading,
          isCommentPluginReady: this.isCommentPanelResizeAllowed,
          wasInFullMode: this.sashIsFullMode,
        });
      }
    }
  }

  private checkMobileSize(): void {
    const viewportWidth: number = this.document.defaultView?.innerWidth ?? 0;
    const wasMobile = this.isMobileSize;
    this.isMobileSize = viewportWidth > 0 && viewportWidth < 768;

    // Reset full mode when switching to mobile view
    if (!wasMobile && this.isMobileSize) {
      this.commentPanelWidthService.handleMobileSizeChange(
        this.isMobileSize,
        this.isCommentsPaneAvailable && this.isCommentExpanded
      );
    } else if (wasMobile && !this.isMobileSize) {
      this.commentPanelWidthService.handleDesktopSizeChange(this.isCommentsPaneAvailable && this.isCommentExpanded);
    }

    this.previousMobileSize = wasMobile;
  }

  get isCommentPanelFullSize(): boolean {
    if (!this.isCommentsPaneAvailable || !this.isCommentExpanded) {
      return false;
    }
    return this.sashIsFullMode;
  }
  get isPlatformCommentExpanded(): boolean {
    const activePage = this.appState.getActivePage();
    return !!activePage && this.appState.isCommentPageCommentExpanded(activePage);
  }

  get hasActivePage(): boolean {
    return !!this.appState.getActivePage();
  }

  get isMediaPreviewCommentsChatShowing(): boolean {
    return this.isCommentFeatureAllowed && this.isCommentVisible && !this.isReleasePreview && !this.isDashboard;
  }

  get canShowCommentIcon(): boolean {
    return (
      this.isCommentFeatureAllowed &&
      this.appState.isEnableCommentPage() &&
      !!this.appState.getActivePage() &&
      !this.isBrandlessBrandPage
    );
  }

  /** The brand page for an account in no brand: it hosts no comment or task panel. */
  get isBrandlessBrandPage(): boolean {
    return isBrandlessBrandPage(this.router.url, this.platformChatState.getCurrentValue().driveAnchorUuid);
  }

  get isNavigatingToProjectDashboard(): boolean {
    return this.appState.isNavigatingToProjectDashboard();
  }

  get treeState(): TreeState<BaseTreeNode> {
    return this.treeManagerService.getCurrentState();
  }

  get isCommentVisible(): boolean {
    if (!this.isCommentFeatureAllowed || !this.treeState?.selectedNode) {
      return false;
    }
    const { selectedNode } = this.treeState;
    return (
      isSubsectionFileNode(selectedNode) ||
      (isShareLinkAssetNode(selectedNode) && selectedNode.assetType === ShareLinkAssetEntityTypes.FILE)
    );
  }

  get isPlatformCommentsChatShowing(): boolean {
    return (
      this.isCommentFeatureAllowed &&
      !this.publicReviewLink &&
      !this.publicPage &&
      !!this.authUser &&
      this.canShowCommentIcon
    );
  }

  get isDashboard(): boolean {
    return this.appState.getActivePage() === ActivePage.ProjectDashboard;
  }

  get isCommentExpanded(): boolean {
    const activePage = this.appState.getActivePage();
    return (
      (this.isPlatformCommentsChatShowing &&
        (activePage ? this.appState.isCommentPageCommentExpanded(activePage) : false)) ||
      (!this.subsectionService.uiState?.commentsCollapsed && this.isMediaPreviewCommentsChatShowing)
    );
  }

  get isCommentsPaneAvailable(): boolean {
    return (
      (this.isPlatformCommentsChatShowing && !this.isMediaPreviewCommentsChatShowing) ||
      this.isMediaPreviewCommentsChatShowing
    );
  }

  get isCommentPluginReady(): boolean {
    return this.appState.getCommentPluginReady();
  }

  /** Theme keeps this template after first plugin ready; it is cleared only when leaving preview. */
  get hasMediaPreviewCommentsTemplate(): boolean {
    return !!this.themeOptions?.mediaPreviewCommentsChat;
  }

  /**
   * Media preview may clear the annotation plugin while switching assets; the comments slot stays mounted.
   * Platform chat still requires the global plugin-ready flag for resize; the task panel does not
   * depend on the comment plugin (never mounted on drive pages).
   */
  get isCommentPanelResizeAllowed(): boolean {
    if (this.isTaskPanelOpen) {
      return true;
    }
    return this.isMediaPreviewCommentsChatShowing
      ? this.hasMediaPreviewCommentsTemplate
      : this.isCommentPluginReady;
  }

  get projectAvatarUrl(): string {
    return this.projectService?.project?.avatarUrl;
  }

  get projectName(): string {
    return this.projectService?.project?.name;
  }

  /**
   * Show project breadcrumb only on:
   * - Project dashboard
   * - Project subsection and its child pages
   */
  get showProjectBreadcrumb(): boolean {
    // Drive mode has no legacy section/subsection tree; the flat breadcrumb shows the drive path instead.
    return !this.themeOptions?.driveMode && (this.isDashboard || this.isOnProjectSubsection);
  }

  get canEnableResize(): boolean {
    return (
      this.isCommentsPaneAvailable &&
      this.isCommentExpanded &&
      !this.isMobileSize &&
      this.isCommentPanelResizeAllowed &&
      !this.isCommentLoading
    );
  }

  /** An org page (brand / drive workspace / campaign) showing the drive comment shell — the `.drive-org-fill` row. */
  get isDriveOrgRow(): boolean {
    return this.isOrgDrivePage && !!this.platformChatState.getCurrentValue().driveAnchorUuid;
  }

  /** Org drive feed (brand / drive workspace / campaign) open beside the content column: the sash resizes it. */
  get isOrgFeedResizable(): boolean {
    return this.isDriveOrgRow && !this.isTaskPanelOpen && this.isOrgDriveCommentsOpen && !this.isMobileSize;
  }

  /** The org feed shares the comment / task width, capped below full-size mode, which it has none of. */
  get orgFeedWidth(): number {
    return Math.min(this.commentPanelWidth, this.toleranceWidth);
  }

  get isSashEnabled(): boolean {
    return this.isOrgFeedResizable || (this.canEnableResize && !this.shouldHideCommentWrapper);
  }

  get sashWidthPx(): number {
    return this.isOrgFeedResizable ? this.orgFeedWidth : this.commentPanelWidth;
  }

  /** No viewport cap for the org feed, so the sash never snaps it to full size; the service clamps it. */
  get sashMaxViewportPercent(): number {
    return this.isOrgFeedResizable ?Number.POSITIVE_INFINITY : this.maxViewportPercent;
  }

  get toleranceWidth(): number {
    return this.commentPanelWidthService.getToleranceWidth();
  }

  get shouldShowBlur(): boolean {
    return this.commentPanelWidthService.shouldShowBlur();
  }

  private get commentWrapperContext(): CommentWrapperContext {
    return {
      hasActivePage: this.hasActivePage,
      commentsCollapsed: this.commentsCollapsed,
      isNavigatingToProjectDashboard: this.isNavigatingToProjectDashboard,
      isCommentLoading: this.isCommentLoading,
      isCommentsPaneAvailable: this.isCommentsPaneAvailable,
      isCommentExpanded: this.isCommentExpanded,
      isPlatformCommentsChatShowing: this.isPlatformCommentsChatShowing,
      isMediaPreviewCommentsChatShowing: this.isMediaPreviewCommentsChatShowing,
      hasMediaPreviewCommentsTemplate: this.hasMediaPreviewCommentsTemplate,
      isCommentPanelFullSize: this.isCommentPanelFullSize,
      isTransitioningFullSize: this.isTransitioningFullSize,
      isResizing: this.isResizing,
      isCommentPluginReady: this.isCommentPluginReady,
      commentPanelWidth: this.commentPanelWidth,
    };
  }

  get commentWrapperStyle(): { [key: string]: string } {
    return this.commentComponentViewService.getCommentWrapperStyle(this.commentWrapperContext);
  }

  get commentWrapperClassMap(): Record<string, boolean> {
    return this.commentComponentViewService.getCommentWrapperClassMap(this.commentWrapperContext);
  }

  get commentWrapperWidth(): number {
    const calculatedWidth = this.getCalculatedCommentWrapperWidth();
    return this.commentPanelWidthService.getAnimatedWrapperWidth(calculatedWidth);
  }

  get taskWrapperWidth(): number {
    const calculatedWidth = this.getCalculatedCommentWrapperWidth();
    return this.commentPanelWidthService.getAnimatedWrapperWidth(calculatedWidth);
  }

  get rightViewMode(): string {
    return this.subsectionService.uiState?.rightPanelView;
  }

  /** Rail state of the org-level drive comment shell — pads the content column. */
  get isOrgDriveCommentsOpen(): boolean {
    return this.driveOrgCommentsUi.open();
  }

  /**
   * The task panel holds the right-hand slot. Comments and tasks share that slot (the
   * top-nav Task button swaps the view), so the org drive comment shell steps aside —
   * hidden, not destroyed, so a draft or open thread survives — while it is open.
   */
  get isTaskPanelOpen(): boolean {
    return this.rightViewMode === 'task' && this.isCommentExpanded;
  }

  get showDriveSurface(): boolean {
    return this.projectService.project?.mode === 'drive';
  }

  /**
   * The pages whose drive anchor the org comment shell serves: the brand page (`/workspaces`),
   * a drive workspace and a campaign overview. Gating on the route rather than on the anchor
   * alone keeps the shell (and its right rail) off pages with no entity — settings and the
   * like — since an org page's anchor outlives the page in the chat context.
   *
   * The page list lives in `org-entity-routes.ts`, next to each page's entity type and the
   * builder that addresses it from an anchor uuid — those three have to agree, so a new org
   * page is one entry there and nothing here.
   */
  get isOrgDrivePage(): boolean {
    return isOrgDriveUrl(this.router.url);
  }

  get enableTaskFeature() {
    // const urlParams = new URLSearchParams(window.location.search);
    // const enableTask = urlParams.get('enableTask');
    // if (enableTask && enableTask.toLowerCase() === 'true') {
    //   return true;
    // }
    // return false;
    return true;
  }

  ngOnInit() {
    this.checkMobileSize();
    this.publicPage = this.activatedRoute.snapshot.data.publicPage;
    this.setupRouterEvents();
    this.cacheBuster = Date.now().toString();
    this.authUser = this.authService.getAuthUser();
    this.authUserSetting = this.authService.getAuthUserSettings();
    this.isAgencyUser = this.authUser?.userPermissionType === 'AGENCY';
    const token = this.authService.getAuthToken();
    if (token) {
      if (!this.isAgencyUser) {
        this.fetchReportMenus();
      }
      this.fetchSelfTrainerMenus();

      // Brand list for the switcher (breadcrumb + sidebar). Never on public review/share links.
      const routeData = this.activatedRoute.snapshot.data;
      if (!this.publicPage && !routeData.publicReviewLink && !routeData.publicShareLink) {
        this.brandService.loadUserBrands();
      }
    }

    // Always enable floating menu
    this.renderer.addClass(this.document.body, 'floating-menu-active');

    if (!this.authUser) {
      EmitterService.get('authUser.changeAvatar')
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((data) => {
          this.authUser = this.authService.getAuthUser();
        });
    }

    this.activatedRoute.data.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data) => {
      this.publicReviewLink = data.publicReviewLink;
      this.publicShareLink = data.publicShareLink;
    });

    EmitterService.get('reportingView')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (data) => {
          this.isReportingView = data;
        },
      });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.isCampaignDrivePath = this.checkCampaignDrivePath(event.urlAfterRedirects);
      this.isComparePage = this.checkComparePath(event.urlAfterRedirects);
      this.isTasksPage = this.checkTasksPath(event.urlAfterRedirects);
      this.updateCommentFeatureVisibility(event.urlAfterRedirects);
    });
    this.isCampaignDrivePath = this.checkCampaignDrivePath(this.router.url);
    this.isComparePage = this.checkComparePath(this.router.url);
    this.isTasksPage = this.checkTasksPath(this.router.url);
    this.updateCommentFeatureVisibility(this.router.url);

    EmitterService.get('user.switched')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        setTimeout(() => {
          this.authUser = this.authService.getAuthUser();
          this.isAgencyUser = this.authUser.userPermissionType === 'AGENCY';
          const token = this.authService.getAuthToken();
          if (token) {
            if (!this.isAgencyUser) {
              this.fetchReportMenus();
            }
            this.fetchSelfTrainerMenus();
          }
        }, 300);
      });

    this.subsectionService.uiState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        distinctUntilChanged((prev, curr) => prev?.commentsCollapsed === curr?.commentsCollapsed)
      )
      .subscribe({
        next: (state) => {
          this.commentsCollapsed = state?.commentsCollapsed;

          const activePage = this.appState.getActivePage();
          if (activePage && this.isMediaPreviewCommentsChatShowing) {
            const shouldBeExpanded = !this.commentsCollapsed;
            const currentExpanded = this.appState.isCommentPageCommentExpanded(activePage);
            if (currentExpanded !== shouldBeExpanded) {
              this.appState.setCommentPageCommentExpanded(activePage, shouldBeExpanded);
            }
          }
        },
      });

    this.subsectionService.uiState$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((state) => {
      this.isShareLinkPreview = !!state?.isShareLinkPreview;
      this.isReleasePreview = !!state?.isReleasePreview;
    });
  }

  ngDoCheck(): void {
    this.releaseReservedCommentPanelWidthIfReady();
    this.syncPlatformChatBodyClass();
    this.syncMediaPreviewCollapsedWithGlobalCommentState();
    this.resetCommentPanelWidthOnToggle();
    this.syncCommentPanelFullSize();
    this.syncCommentWrapperVisibility();
  }

  private setupRouterEvents(): void {
    this.routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.modalService.dismissAll();

        // Page changes only: panel toggles rewrite `?ctx=` through the router, and reserving the
        // width then kept a just-closed panel full width, hiding the page under it.
        if (this.isCommentExpanded && urlPath(event.url) !== urlPath(this.router.url)) {
          this.reserveCommentPanelWidthDuringNavigation = true;

          if (this.reserveCommentPanelWidthTimeoutId !== null) {
            globalThis.clearTimeout(this.reserveCommentPanelWidthTimeoutId);
          }

          this.reserveCommentPanelWidthTimeoutId = globalThis.setTimeout(() => {
            this.reserveCommentPanelWidthDuringNavigation = false;
            this.reserveCommentPanelWidthTimeoutId = null;
            this.cdr.markForCheck();
          }, 3000);
        }
      }
    });
  }

  private releaseReservedCommentPanelWidthIfReady(): void {
    if (!this.reserveCommentPanelWidthDuringNavigation) {
      return;
    }

    if (this.isMediaPreviewCommentsChatShowing) {
      if (this.isCommentPanelResizeAllowed && !this.isCommentLoading) {
        const activePage = this.appState.getActivePage();
        if (activePage && activePage !== ActivePage.ProjectDashboard) {
          const expectedExpanded = this.appState.isCommentPageCommentExpanded(activePage);
          const actualExpanded = !this.commentsCollapsed;

          if (expectedExpanded === actualExpanded) {
            this.reserveCommentPanelWidthDuringNavigation = false;
            if (this.reserveCommentPanelWidthTimeoutId !== null) {
              globalThis.clearTimeout(this.reserveCommentPanelWidthTimeoutId);
              this.reserveCommentPanelWidthTimeoutId = null;
            }
          }
        }
      }
      return;
    }

    if (this.isDashboard) {
      return;
    }

    if (!this.isCommentExpanded) {
      if (this.isCommentFeatureAllowed) {
        return;
      }
    }

    if (this.isCommentExpanded || !this.isCommentFeatureAllowed) {
      this.reserveCommentPanelWidthDuringNavigation = false;
      if (this.reserveCommentPanelWidthTimeoutId !== null) {
        globalThis.clearTimeout(this.reserveCommentPanelWidthTimeoutId);
        this.reserveCommentPanelWidthTimeoutId = null;
      }
    }
  }

  private syncMediaPreviewCollapsedWithGlobalCommentState(): void {
    const activePage = this.appState.getActivePage();
    if (!activePage) {
      return;
    }

    if (!this.isMediaPreviewCommentsChatShowing) {
      return;
    }

    const shouldBeExpanded = this.appState.isCommentPageCommentExpanded(activePage);
    const shouldBeCollapsed = !shouldBeExpanded;

    if (this.subsectionService.uiState?.commentsCollapsed !== shouldBeCollapsed) {
      this.subsectionService.updateUIState({ commentsCollapsed: shouldBeCollapsed });
    }
  }

  private syncCommentPanelFullSize(): void {
    const currentFullSize = this.isCommentPanelFullSize;
    this.commentPanelWidthService.handleFullSizeStateChange(currentFullSize);
  }

  private resetCommentPanelWidthOnToggle(): void {
    const currentExpandedState = this.isCommentExpanded;
    this.commentPanelWidthService.handleExpandedStateChange(currentExpandedState);
  }

  private syncCommentWrapperVisibility(): void {
    const calculatedWidth = this.getCalculatedCommentWrapperWidth();

    this.commentPanelWidthService.syncWrapperOpenAnimation(calculatedWidth);
    const isSwap = this.driveRightRail.swapping();
    this.shouldHideCommentWrapper = this.commentPanelWidthService.syncWrapperVisibility(calculatedWidth, isSwap).shouldHide;
    this.shouldHideTaskWrapper = this.commentPanelWidthService.syncWrapperVisibility(calculatedWidth, isSwap).shouldHide;
  }

  private fetchReportMenus() {
    this.reportingService
      .getReportMenus(true)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.hasReportMenus = response?.menus?.length > 0;
        },
        error: (error) => {
          console.error('Error fetching report menus:', error);
          this.hasReportMenus = false;
        },
      });
  }

  private fetchSelfTrainerMenus() {
    this.selfTrainerV2Service
      .getSelfTrainerList(true)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response) => {
          this.hasSelfTrainerMenus = response?.menus?.length > 0;
        },
        error: (error) => {
          console.error('Error fetching self trainer menus:', error);
          this.hasSelfTrainerMenus = false;
        },
      });
  }

  private checkCampaignDrivePath(url: string): boolean {
    return url.includes('/workspaces/campaign-drive') || url.includes('/workspaces/campaigndrive/dashboard');
  }

  private checkComparePath(url: string): boolean {
    return url.includes('compare');
  }

  private checkTasksPath(url: string): boolean {
    return url.includes('/tasks');
  }

  private updateCommentFeatureVisibility(url: string): void {
    // My Own Space has the drive explorer's own feed and no tasks, so neither the
    // platform comment nor the task panel takes width there (mirrors TopNavComponent).
    this.isCommentFeatureAllowed = !this.isMySpacePath(url) && this.commentVisibilityService.isCommentAllowed(url);
  }

  private isMySpacePath(url: string): boolean {
    return /^\/my-space(\/|$|\?)/.test(url);
  }

  onLeftMenuOpen() {
    setTimeout(() => {
      this.openedLeftMenu = true;
    }, 0);
  }

  toggleSidebar() {
    this.leftSidebarFloatingComponent.toggleSidebar();
  }

  toggleMobileSearch() {
    this.showMobileSearch = !this.showMobileSearch;
  }

  closeMobileSearch() {
    this.showMobileSearch = false;
  }

  goToInitialPage() {
    this.workspaceService.unsetActiveWorkspace();
    this.router.navigate(['/workspaces']).then(() => {
      this.workspaceService.refreshWorkspaceData();
    });
  }

  navigateToPrevious(): void {
    this.treeManagerService.navigateToPrevious(true);
  }

  navigateToNext(): void {
    this.treeManagerService.navigateToNext(true);
  }

  navigateToNode(node: BaseTreeNode): void {
    this.treeManagerService.navigateToNode(node);
  }

  private syncPlatformChatBodyClass(): void {
    const shouldApply = this.isPlatformCommentsChatShowing && !this.isMediaPreviewCommentsChatShowing;
    if (shouldApply && !this.isPlatformChatBodyClassApplied) {
      this.renderer.addClass(this.document.body, this.platformChatBodyClass);
      this.isPlatformChatBodyClassApplied = true;
    } else if (!shouldApply && this.isPlatformChatBodyClassApplied) {
      this.renderer.removeClass(this.document.body, this.platformChatBodyClass);
      this.isPlatformChatBodyClassApplied = false;
    }
  }

  onCommentPanelWidthChange(newWidth: number): void {
    if (this.isOrgFeedResizable) {
      this.commentPanelWidthService.handleCappedWidthChange(newWidth);
      return;
    }
    this.commentPanelWidthService.handleWidthChange(newWidth, {
      isCommentLoading: this.isCommentLoading,
      isCommentPluginReady: this.isCommentPanelResizeAllowed,
      wasInFullMode: this.sashIsFullMode,
    });
  }

  onCommentLoadingChange(isLoading: boolean): void {
    this.isCommentLoading = isLoading;
  }

  onResetClicked(): void {
    this.commentPanelWidthService.handleReset();
  }

  onCommentWrapperTransitionEnd(event: TransitionEvent): void {
    this.shouldHideCommentWrapper = this.commentPanelWidthService.handleWrapperTransitionEnd(
      event,
      this.commentWrapperWidth
    ).shouldHide;
    this.shouldHideTaskWrapper = this.commentPanelWidthService.handleWrapperTransitionEnd(
      event,
      this.commentWrapperWidth
    ).shouldHide;
  }

  ngOnDestroy() {
    this.commentPanelWidthService.cleanup();
    if (this.reserveCommentPanelWidthTimeoutId !== null) {
      globalThis.clearTimeout(this.reserveCommentPanelWidthTimeoutId);
      this.reserveCommentPanelWidthTimeoutId = null;
    }
    this.renderer.removeClass(this.document.body, 'floating-menu-active');
    this.renderer.removeClass(this.document.body, this.platformChatBodyClass);
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  private getCalculatedCommentWrapperWidth(): number {
    return this.commentPanelWidthService.getCalculatedWrapperWidth({
      isCommentFeatureAllowed: this.isCommentFeatureAllowed,
      reserveCommentPanelWidthDuringNavigation: this.reserveCommentPanelWidthDuringNavigation,
      commentPanelWidth: this.commentPanelWidth,
      context: this.commentWrapperContext,
    });
  }
}
